import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {NotificationService} from "../../services/notification-service/notification.service";
import {FileService} from "../../services/file-service/file.service";
import {ConfirmDeleteImageComponent} from "../confirm-delete-image/confirm-delete-image.component";
import { Inject } from '@angular/core';
import { MEntryService } from '../../services/m-entry-service/m-entry.service';
import { PhotoAnnotateDialogComponent, PhotoAnnotateDialogData } from '../photo-annotate/photo-annotate-dialog.component';

/** DOCU-1143: the lightbox closes with this when an annotation was saved or discarded. */
export const LIGHTBOX_PHOTOS_CHANGED = 'photos-changed';

@Component({
  selector: 'app-image-lightbox',
  templateUrl: './image-lightbox.component.html',
  styleUrl: './image-lightbox.component.scss'
})
export class ImageLightboxComponent implements OnInit {
  @ViewChild('imageContainer') imageContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('imageElement') imageElement!: ElementRef<HTMLImageElement>;

  constructor(
    public dialogRef: MatDialogRef<ImageLightboxComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImageLightboxData,
    private dialog : MatDialog,
    private fileService : FileService,
    private notificationService : NotificationService,
    private entryService : MEntryService
  ) {  }

  discarding = false;

  loading = false;
  currentIndex: number = 0;
  allImages: ImageLightboxData[] = [];
  isZoomed = false;

  private scale = 1;
  private translateX = 0;
  private translateY = 0;
  private isDragging = false;
  private hasDragged = false;
  private startX = 0;
  private startY = 0;
  private readonly ZOOM_SCALE = 2.5;

  ngOnInit() {
    if (this.data.allImages && this.data.allImages.length > 0) {
      this.allImages = this.data.allImages;
      this.currentIndex = this.allImages.findIndex(img => img.fileId === this.data.fileId);
      if (this.currentIndex === -1) this.currentIndex = 0;
    } else {
      this.allImages = [this.data];
      this.currentIndex = 0;
    }
  }

  get currentImage(): ImageLightboxData {
    return this.allImages[this.currentIndex];
  }

  get hasPrevious(): boolean {
    return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return this.currentIndex < this.allImages.length - 1;
  }

  get allowDelete(): boolean {
    return this.data.allowDelete === true;
  }

  /** DOCU-1143: annotating needs the entry the photo belongs to. */
  get canAnnotate(): boolean {
    return this.data.annotatable === true && !!this.data.entryId && !this.isOrphanedCopy;
  }

  /** DOCU-1143: the current image is an annotated copy whose original is still there. */
  get isAnnotatedCopy(): boolean {
    return !!this.currentImage.derivedFrom && !this.isOrphanedCopy;
  }

  /**
   * DOCU-1143: a copy whose original was deleted is a photo of its own — there is nothing left to
   * re-annotate from or to fall back to, so it gets neither "Neu markieren" nor "Verwerfen".
   */
  private get isOrphanedCopy(): boolean {
    const originalId = this.currentImage.derivedFrom;
    return !!originalId && !this.allImages.some(i => i.fileId === originalId);
  }

  /**
   * DOCU-1143: opens the editor. It always draws on the original — for a copy, too, so marks never
   * stack up; saving then replaces the existing copy at its place.
   */
  annotate() {
    const image = this.currentImage;
    const originalId = image.derivedFrom ?? image.fileId;
    const replacesCopy = !!image.derivedFrom || this.allImages.some(i => i.derivedFrom === image.fileId);

    this.dialog
      .open(PhotoAnnotateDialogComponent, {
        width: '95vw',
        maxWidth: '95vw',
        maxHeight: '95vh',
        data: { entryId: this.data.entryId!, fileId: originalId, replacesCopy } as PhotoAnnotateDialogData
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) this.dialogRef.close(LIGHTBOX_PHOTOS_CHANGED);
      });
  }

  /** DOCU-1143: deletes the annotated copy; the original is shown in the export again. */
  discardAnnotation() {
    if (this.discarding || !this.isAnnotatedCopy) return;
    this.discarding = true;
    this.entryService.deleteAnnotatedCopy(this.data.entryId!, this.currentImage.fileId).subscribe({
      next: () => {
        this.discarding = false;
        this.notificationService.showSuccess('AUDIT.ANNOTATE.DISCARDED');
        this.dialogRef.close(LIGHTBOX_PHOTOS_CHANGED);
      },
      error: () => {
        this.discarding = false;
        this.notificationService.showError('AUDIT.ANNOTATE.DISCARD_ERROR');
      }
    });
  }

  previousImage() {
    if (this.hasPrevious) {
      this.currentIndex--;
      this.resetZoom();
    }
  }

  nextImage() {
    if (this.hasNext) {
      this.currentIndex++;
      this.resetZoom();
    }
  }

  deleteImage(){
    const dialogRef = this.dialog.open(ConfirmDeleteImageComponent, {
      width: '30vw',
      maxWidth: '80vw',
      data: this.currentImage
    });
    dialogRef.afterClosed().subscribe(() => {
      this.dialogRef.close()
    })
  }

  onImageClick(event: MouseEvent) {
    if (this.hasDragged) {
      this.hasDragged = false;
      return;
    }

    const container = this.imageContainer.nativeElement;
    const img = this.imageElement.nativeElement;

    if (!this.isZoomed) {
      const rect = img.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const xPercent = x / rect.width;
      const yPercent = y / rect.height;

      this.scale = this.ZOOM_SCALE;
      this.translateX = -(xPercent - 0.5) * rect.width * (this.ZOOM_SCALE - 1);
      this.translateY = -(yPercent - 0.5) * rect.height * (this.ZOOM_SCALE - 1);
      this.isZoomed = true;
      
      this.updateTransform();
    } else {
      this.resetZoom();
    }
  }

  onMouseDown(event: MouseEvent) {
    if (!this.isZoomed) return;
    this.isDragging = true;
    this.hasDragged = false;
    this.startX = event.clientX - this.translateX;
    this.startY = event.clientY - this.translateY;
    const container = this.imageContainer.nativeElement;
    container.style.cursor = 'grabbing';
    event.preventDefault();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging || !this.isZoomed) return;

    this.hasDragged = true;
    this.translateX = event.clientX - this.startX;
    this.translateY = event.clientY - this.startY;
    
    this.updateTransform();
  }

  onMouseUp() {
    if (this.isDragging) {
      this.isDragging = false;
      const container = this.imageContainer.nativeElement;
      container.style.cursor = this.isZoomed ? 'grab' : 'zoom-in';
    }
  }

  private updateTransform() {
    const img = this.imageElement.nativeElement;
    img.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  private resetZoom() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isZoomed = false;
    this.isDragging = false;
    
    if (this.imageElement) {
      this.updateTransform();
    }
    
    if (this.imageContainer) {
      this.imageContainer.nativeElement.style.cursor = 'zoom-in';
    }
  }
}

export interface ImageLightboxData{
  fileId: string
  image: string
  /** DOCU-1302: small preview for the strip below the image; falls back to `image`. */
  thumb?: string
  allImages?: ImageLightboxData[]
  allowDelete?: boolean
  /** DOCU-1137: hidden in the export — shown with a label. */
  hidden?: boolean
  /** DOCU-1137: comment of the photo group, shown below the image. */
  caption?: string | null
  /** DOCU-1143: entry of the photos — needed to annotate. */
  entryId?: string
  /** DOCU-1143: offer "annotate" (only where the page reloads the layout afterwards). */
  annotatable?: boolean
  /** DOCU-1143: set on an annotated copy — the id of its original. */
  derivedFrom?: string | null
}
